/**
 * Site Context Utility
 * Handles detection of B2B vs B2C site mode based on subdomain
 */

export type SiteMode = 'b2b' | 'b2c';

/**
 * Detects whether the current site is B2B or B2C based on the hostname
 *
 * Detection priority:
 * 1. Production/staging subdomain (shop.infibrite.com)
 * 2. Local development with lvh.me (shop.lvh.me)
 * 3. Fallback: localhost with ?mode=b2c query param
 *
 * @returns 'b2c' if on shop subdomain, 'b2b' otherwise
 */
export const getSiteMode = (): SiteMode => {
  if (typeof window === 'undefined') {
    // Server-side rendering - default to B2B
    return 'b2b';
  }

  const hostname = window.location.hostname;

  // Production/staging subdomains
  if (hostname.startsWith('shop.')) {
    return 'b2c';
  }

  // Check for local development with lvh.me
  // lvh.me is a domain that points to localhost, useful for subdomain testing
  if (hostname.includes('.lvh.me')) {
    if (hostname.startsWith('shop.')) {
      return 'b2c';
    }
    return 'b2b';
  }

  // Fallback for localhost and Vercel preview hosts with a query parameter.
  // Production hostnames are custom domains (shop./www.), so *.vercel.app
  // previews can safely opt into the storefront with ?mode=b2c; the choice
  // persists for the tab via sessionStorage.
  const isPreviewHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.vercel.app');
  if (isPreviewHost) {
    const params = new URLSearchParams(window.location.search);
    const explicitMode = params.get('mode');
    if (explicitMode === 'b2c' || explicitMode === 'b2b') {
      sessionStorage.setItem('infibrite-preview-mode',explicitMode);
      return explicitMode;
    }
    if (sessionStorage.getItem('infibrite-preview-mode') === 'b2c') return 'b2c';
  }

  // Default to B2B
  return 'b2b';
};

/**
 * Checks if B2C shop is enabled via environment variable
 * Use this to implement a "maintenance mode" or soft launch
 *
 * @returns true if B2C is enabled, false otherwise
 */
export const isB2CEnabled = (): boolean => {
  // Default to true if env var is not set (for development)
  const envValue = process.env.REACT_APP_B2C_ENABLED;
  return envValue !== 'false';
};

/**
 * Checks if the current site is B2C
 */
export const isB2CSite = (): boolean => {
  return getSiteMode() === 'b2c';
};

/**
 * Checks if the current site is B2B
 */
export const isB2BSite = (): boolean => {
  return getSiteMode() === 'b2b';
};

/**
 * Gets the appropriate base URL for the current site mode
 */
export const getBaseUrl = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;

  let baseUrl = `${protocol}//${hostname}`;
  if (port && port !== '80' && port !== '443') {
    baseUrl += `:${port}`;
  }

  return baseUrl;
};

/**
 * Gets the B2B site URL from the current site
 * Useful for linking back to B2B from B2C
 */
export const getB2BUrl = (): string => {
  if (typeof window === 'undefined') {
    return 'https://infibrite.com';
  }

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Remove shop. subdomain
  let b2bHostname = hostname.replace(/^shop\./, '');

  // Handle lvh.me for local development
  if (hostname.includes('.lvh.me')) {
    b2bHostname = 'lvh.me';
  }

  let baseUrl = `${protocol}//${b2bHostname}`;
  if (port && port !== '80' && port !== '443') {
    baseUrl += `:${port}`;
  }

  return baseUrl;
};

/**
 * B2C shop URL for a given location (pure; see getB2CUrl).
 * www.infibrite.com -> shop.infibrite.com (not shop.www.infibrite.com).
 */
export const b2cUrlFor = (protocol: string, hostname: string, port: string): string => {
  // Add shop. subdomain if not already present
  let b2cHostname = hostname;
  if (!hostname.startsWith('shop.')) {
    b2cHostname = `shop.${hostname.replace(/^www\./, '')}`;
  }

  // Handle lvh.me for local development
  if (hostname === 'lvh.me') {
    b2cHostname = 'shop.lvh.me';
  }

  let baseUrl = `${protocol}//${b2cHostname}`;
  if (port && port !== '80' && port !== '443') {
    baseUrl += `:${port}`;
  }

  return baseUrl;
};

/**
 * Gets the B2C shop URL from the current site
 * Useful for linking to B2C from B2B
 */
export const getB2CUrl = (): string => {
  if (typeof window === 'undefined') {
    return 'https://shop.infibrite.com';
  }

  return b2cUrlFor(window.location.protocol, window.location.hostname, window.location.port);
};

/**
 * Local storage keys with site-specific prefixes
 * Ensures B2B and B2C carts don't conflict
 */
export const getStorageKey = (key: string): string => {
  const mode = getSiteMode();
  return `infibrite-${mode}-${key}`;
};

/**
 * B2C-specific storage key for cart
 */
export const B2C_CART_STORAGE_KEY = 'infibrite-b2c-cart';

/**
 * B2B-specific storage key for cart (for reference)
 */
export const B2B_CART_STORAGE_KEY = 'infibrite-cart';
